export const WE_RIDER_PROFILE_STATUSES = Object.freeze({
  INCOMPLETE: "incomplete",
  COMPLETE: "complete",
});

export const WE_RIDER_IDENTITY_STATUSES = Object.freeze({
  NOT_STARTED: "not_started",
  PENDING: "pending",
  APPROVED: "approved",
  ACTION_REQUIRED: "action_required",
  REJECTED: "rejected",
  EXPIRED: "expired",
});

export const WE_RIDER_INSURANCE_STATUSES = Object.freeze({
  NOT_STARTED: "not_started",
  QUOTE_PENDING: "quote_pending",
  PAYMENT_PENDING: "payment_pending",
  ISSUANCE_PENDING: "issuance_pending",
  ACTIVE: "active",
  PAST_DUE: "past_due",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
});

export const WE_RIDER_REVIEW_STATUSES = Object.freeze({
  PENDING: "pending",
  APPROVED: "approved",
  ACTION_REQUIRED: "action_required",
  REJECTED: "rejected",
  SUSPENDED: "suspended",
});

export const WE_RIDER_ELIGIBILITY_STATUSES = Object.freeze({
  REGISTRATION_INCOMPLETE: "registration_incomplete",
  IDENTITY_PENDING: "identity_pending",
  IDENTITY_ACTION_REQUIRED: "identity_action_required",
  IDENTITY_REJECTED: "identity_rejected",
  IDENTITY_EXPIRED: "identity_expired",
  INSURANCE_PENDING: "insurance_pending",
  INSURANCE_INACTIVE: "insurance_inactive",
  REVIEW_PENDING: "review_pending",
  REVIEW_ACTION_REQUIRED: "review_action_required",
  REVIEW_REJECTED: "review_rejected",
  ENABLED: "enabled",
  SUSPENDED: "suspended",
});

const PROFILE_STATUS_VALUES = new Set(Object.values(WE_RIDER_PROFILE_STATUSES));
const IDENTITY_STATUS_VALUES = new Set(Object.values(WE_RIDER_IDENTITY_STATUSES));
const INSURANCE_STATUS_VALUES = new Set(Object.values(WE_RIDER_INSURANCE_STATUSES));
const REVIEW_STATUS_VALUES = new Set(Object.values(WE_RIDER_REVIEW_STATUSES));

const pickStatus = (value, allowed, fallback) => {
  const normalized = String(value || "").trim().toLowerCase();
  return allowed.has(normalized) ? normalized : fallback;
};

export const deriveWeRiderEligibility = ({
  profileStatus,
  identityStatus,
  reviewStatus,
} = {}) => {
  const profile = pickStatus(
    profileStatus,
    PROFILE_STATUS_VALUES,
    WE_RIDER_PROFILE_STATUSES.INCOMPLETE
  );
  const identity = pickStatus(
    identityStatus,
    IDENTITY_STATUS_VALUES,
    WE_RIDER_IDENTITY_STATUSES.NOT_STARTED
  );
  const review = pickStatus(
    reviewStatus,
    REVIEW_STATUS_VALUES,
    WE_RIDER_REVIEW_STATUSES.PENDING
  );

  if (review === WE_RIDER_REVIEW_STATUSES.SUSPENDED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.SUSPENDED;
  }
  if (review === WE_RIDER_REVIEW_STATUSES.REJECTED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.REVIEW_REJECTED;
  }
  if (review === WE_RIDER_REVIEW_STATUSES.ACTION_REQUIRED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.REVIEW_ACTION_REQUIRED;
  }
  if (profile !== WE_RIDER_PROFILE_STATUSES.COMPLETE) {
    return WE_RIDER_ELIGIBILITY_STATUSES.REGISTRATION_INCOMPLETE;
  }
  if (identity === WE_RIDER_IDENTITY_STATUSES.REJECTED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_REJECTED;
  }
  if (identity === WE_RIDER_IDENTITY_STATUSES.ACTION_REQUIRED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_ACTION_REQUIRED;
  }
  if (identity === WE_RIDER_IDENTITY_STATUSES.EXPIRED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_EXPIRED;
  }
  if (identity !== WE_RIDER_IDENTITY_STATUSES.APPROVED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_PENDING;
  }
  if (review !== WE_RIDER_REVIEW_STATUSES.APPROVED) {
    return WE_RIDER_ELIGIBILITY_STATUSES.REVIEW_PENDING;
  }
  return WE_RIDER_ELIGIBILITY_STATUSES.ENABLED;
};

export const normalizeWeRiderCompliance = (compliance = {}) => {
  const normalized = {
    profileStatus: pickStatus(
      compliance.profileStatus ?? compliance.profile_status,
      PROFILE_STATUS_VALUES,
      WE_RIDER_PROFILE_STATUSES.INCOMPLETE
    ),
    identityStatus: pickStatus(
      compliance.identityStatus ?? compliance.identity_status,
      IDENTITY_STATUS_VALUES,
      WE_RIDER_IDENTITY_STATUSES.NOT_STARTED
    ),
    insuranceStatus: pickStatus(
      compliance.insuranceStatus ?? compliance.insurance_status,
      INSURANCE_STATUS_VALUES,
      WE_RIDER_INSURANCE_STATUSES.NOT_STARTED
    ),
    reviewStatus: pickStatus(
      compliance.reviewStatus ?? compliance.review_status,
      REVIEW_STATUS_VALUES,
      WE_RIDER_REVIEW_STATUSES.PENDING
    ),
    statusReason: String(
      compliance.statusReason ?? compliance.status_reason ?? ""
    ).trim(),
    updatedAt:
      compliance.updatedAt ?? compliance.updated_at ?? null,
  };

  return {
    ...normalized,
    eligibilityStatus: deriveWeRiderEligibility(normalized),
    canOperate:
      deriveWeRiderEligibility(normalized) ===
      WE_RIDER_ELIGIBILITY_STATUSES.ENABLED,
  };
};

export const buildInitialWeRiderCompliance = ({ profileComplete = false } = {}) =>
  normalizeWeRiderCompliance({
    profileStatus: profileComplete
      ? WE_RIDER_PROFILE_STATUSES.COMPLETE
      : WE_RIDER_PROFILE_STATUSES.INCOMPLETE,
    identityStatus: WE_RIDER_IDENTITY_STATUSES.NOT_STARTED,
    insuranceStatus: WE_RIDER_INSURANCE_STATUSES.NOT_STARTED,
    reviewStatus: WE_RIDER_REVIEW_STATUSES.PENDING,
  });

export const buildApprovedWeRiderCompliance = () =>
  normalizeWeRiderCompliance({
    profileStatus: WE_RIDER_PROFILE_STATUSES.COMPLETE,
    identityStatus: WE_RIDER_IDENTITY_STATUSES.APPROVED,
    insuranceStatus: WE_RIDER_INSURANCE_STATUSES.NOT_STARTED,
    reviewStatus: WE_RIDER_REVIEW_STATUSES.APPROVED,
  });

export const WE_RIDER_ELIGIBILITY_COPY = Object.freeze({
  [WE_RIDER_ELIGIBILITY_STATUSES.REGISTRATION_INCOMPLETE]: {
    label: "Registro incompleto",
    description: "Completa tus datos personales y del vehículo.",
    tone: "warning",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_PENDING]: {
    label: "Identidad pendiente",
    description: "Falta presentar o validar tu identidad.",
    tone: "warning",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_ACTION_REQUIRED]: {
    label: "Necesitamos información",
    description: "Revisa la observación y vuelve a presentar la documentación.",
    tone: "danger",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_REJECTED]: {
    label: "Identidad rechazada",
    description: "La validación de identidad no fue aprobada.",
    tone: "danger",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.IDENTITY_EXPIRED]: {
    label: "Identidad vencida",
    description: "Debes actualizar la documentación de identidad.",
    tone: "danger",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.INSURANCE_PENDING]: {
    label: "Seguro pendiente",
    description: "Falta contratar o emitir el seguro del rider.",
    tone: "warning",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.INSURANCE_INACTIVE]: {
    label: "Seguro inactivo",
    description: "El seguro está vencido, impago o cancelado.",
    tone: "danger",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.REVIEW_PENDING]: {
    label: "Revisión pendiente",
    description: "MozzoApp está revisando tu habilitación.",
    tone: "warning",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.REVIEW_ACTION_REQUIRED]: {
    label: "Necesitamos informacion",
    description: "Revisa la observacion y vuelve a presentar los datos solicitados.",
    tone: "danger",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.REVIEW_REJECTED]: {
    label: "Habilitacion rechazada",
    description: "La revision administrativa no fue aprobada.",
    tone: "danger",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.ENABLED]: {
    label: "Habilitado",
    description: "Tu identidad fue aprobada y ya puedes recibir viajes.",
    tone: "success",
  },
  [WE_RIDER_ELIGIBILITY_STATUSES.SUSPENDED]: {
    label: "Cuenta suspendida",
    description: "No puedes recibir viajes hasta resolver la suspensión.",
    tone: "danger",
  },
});

export const getWeRiderEligibilityCopy = (status) =>
  WE_RIDER_ELIGIBILITY_COPY[status] ||
  WE_RIDER_ELIGIBILITY_COPY[
    WE_RIDER_ELIGIBILITY_STATUSES.REGISTRATION_INCOMPLETE
  ];
